package at.theatervogelweide.domain

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.atTime
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/**
 * How the days entered for a news item map to its `activeFrom` and `activeTo` (#21). Both days are whole days in the
 * theater's local time and both are inclusive: "Veröffentlicht bis 31.10." shows the item on the 31st and hides it from the
 * 1st of November. All timestamps are in UTC, as stored in the database.
 */
object NewsPeriod {

    /** The start of [firstDay] in theater time. */
    fun activeFrom(firstDay: LocalDate): LocalDateTime {
        return TheaterTime.toUtc(firstDay.atTime(LocalTime(0, 0)))
    }

    /** The start of the day after [lastDay] in theater time, so the item is still shown on [lastDay]. */
    fun activeTo(lastDay: LocalDate): LocalDateTime {
        return TheaterTime.toUtc(lastDay.plus(DatePeriod(days = 1)).atTime(LocalTime(0, 0)))
    }

    /** The day [activeFrom] falls on in theater time. */
    fun firstDay(activeFrom: LocalDateTime): LocalDate {
        return TheaterTime.fromUtc(activeFrom).date
    }

    /**
     * The last day the item is shown on: the day before [activeTo] if it is at midnight in theater time, otherwise the
     * day of [activeTo] itself, on which the item is shown until that time.
     */
    fun lastDay(activeTo: LocalDateTime): LocalDate {
        val local = TheaterTime.fromUtc(activeTo)
        return if (local.time == MIDNIGHT) local.date.minus(DatePeriod(days = 1)) else local.date
    }

    private val MIDNIGHT = LocalTime(0, 0)
}
